import * as React from 'react';

import { matchText } from '../../Select.helpers';

import type { SelectOption, SelectRootProps } from '../../Select.types';

export const useFilterOptions = <OptionType extends SelectOption>(
    options: OptionType[],
    searchValue?: string,
    filterOption?: SelectRootProps['filterOption'],
    optionFilterProp?: keyof OptionType
) =>
    React.useMemo(() => {
        if (!searchValue || filterOption === false) {
            return options;
        }

        const filteredOptions: OptionType[] = [];

        const customizeFilter = typeof filterOption === 'function';

        const upperSearch = searchValue.toUpperCase();
        const filterFunc = customizeFilter
            ? filterOption
            : (_: string, option: OptionType) => {
                  // Use provided `optionFilterProp`
                  if (optionFilterProp) {
                      return matchText(option[optionFilterProp], upperSearch);
                  }

                  // Auto select `label` or `value` by option type
                  if (option.options) {
                      // hack `fieldLabel` since `OptionGroup` children is not `label`
                      return matchText(option.label, upperSearch);
                  }

                  return matchText(option.value, upperSearch);
              };

        options.forEach((item) => {
            // Group should check child options
            if (item.options) {
                // Check group first
                const matchGroup = filterFunc(searchValue, item);
                if (matchGroup) {
                    filteredOptions.push(item);
                } else {
                    // Check option
                    const subOptions = item.options.filter((subItem) =>
                        filterFunc(searchValue, { ...subItem, ...subItem.original })
                    );
                    if (subOptions.length) {
                        filteredOptions.push({
                            ...item,
                            options: subOptions
                        });
                    }
                }

                return;
            }

            if (filterFunc(searchValue, { ...item, ...item.original })) {
                filteredOptions.push(item);
            }
        });

        return filteredOptions;
    }, [options, filterOption, optionFilterProp, searchValue]);
